import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuthStore } from '@/store/useAuthStore';
import { fetchMeinTarif } from '@/api/trees.api';
import { groesse } from '@/utils/format';
import {
  setup2fa, verifySetup2fa, disable2fa,
  getErsatzcodeStand, neueErsatzcodes,
  getWebauthnRegisterOptions, verifyWebauthnRegistration,
  getWebauthnCredentials, deleteWebauthnCredential,
  getRundnachrichten, setRundnachrichten,
  type WebauthnCredentialInfo,
} from '@/api/auth.api';
import { useConfirm } from '@/components/common/ConfirmDialog';

// ─── QR-Code Komponente ─────────────────────────────────────────────────────
// `src` ist ein serverseitig erzeugtes data:image-Data-URL. Der QR-Code wird
// NICHT mehr von einem Drittanbieter geladen – das TOTP-Secret bleibt lokal.

function QrCode({ src }: { src: string }) {
  return (
    <div className="flex flex-col items-center gap-4">
      <img src={src} alt="QR-Code zur Einrichtung der Authenticator-App" className="h-48 w-48 rounded-lg border border-slate-200 bg-white p-2" />
      <p className="text-center text-xs text-slate-400">Scanne den QR-Code mit deiner Authenticator-App</p>
    </div>
  );
}

// ─── Ersatzcodes ────────────────────────────────────────────────────────────
//
// Wer sein Telefon verliert, verliert seinen zweiten Faktor – und ohne Ersatz
// ist das Konto zu. Diese zehn Einmal-Codes sind der Weg zurück.
//
// Sie stehen hier EINMAL. Danach gibt es sie nirgends mehr: gespeichert wird
// nur ein Hash, so wie beim Passwort. Deshalb sagt der Kasten das auch
// deutlich, statt es als Feinheit unter den Tisch fallen zu lassen – wer ihn
// wegklickt, ohne die Codes zu sichern, bekommt sie nicht zurück.

function Ersatzcodes({ codes, onFertig }: { codes: string[]; onFertig?: () => void }) {
  const alsText = codes.join('\n');

  const herunterladen = () => {
    const blatt =
      'Ersatzcodes für die Zwei-Faktor-Anmeldung\n' +
      '========================================\n\n' +
      'Jeder Code gilt genau EINMAL. Bewahre dieses Blatt so auf, wie du\n' +
      'einen Ersatzschlüssel zu deiner Wohnung aufbewahren würdest.\n\n' +
      `${alsText}\n`;
    const url = URL.createObjectURL(new Blob([blatt], { type: 'text/plain;charset=utf-8' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = 'ersatzcodes.txt';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="rounded-lg border border-amber-300 bg-amber-50 p-4">
      <h4 className="font-medium text-amber-900">Deine Ersatzcodes</h4>
      <p className="mt-1 text-sm text-amber-800">
        Jeder Code gilt genau einmal und ersetzt bei der Anmeldung den Code aus der App.
        <strong> Sie werden nur jetzt angezeigt</strong> – danach lassen sie sich nicht mehr
        abrufen, nur neu erzeugen.
      </p>

      <ul className="mt-3 grid grid-cols-1 gap-1 rounded-md border border-amber-200 bg-white p-3 font-mono text-sm text-slate-900 sm:grid-cols-2">
        {codes.map((c) => (
          <li key={c} className="select-all tracking-wider">{c}</li>
        ))}
      </ul>

      <div className="mt-3 flex flex-wrap gap-2">
        <button
          type="button"
          onClick={herunterladen}
          className="rounded-md bg-amber-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-amber-700"
        >
          Als Datei speichern
        </button>
        <button
          type="button"
          onClick={() => {
            navigator.clipboard.writeText(alsText).then(
              () => toast.success('Codes kopiert.'),
              () => toast.error('Kopieren nicht möglich – bitte die Datei speichern.'),
            );
          }}
          className="rounded-md border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
        >
          Kopieren
        </button>
        {onFertig && (
          <button
            type="button"
            onClick={onFertig}
            className="rounded-md border border-amber-300 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100"
          >
            Ich habe sie gesichert
          </button>
        )}
      </div>
    </div>
  );
}

/**
 * Restanzahl und „neu erzeugen".
 *
 * Ohne diese Anzeige merkt man erst beim letzten Code, dass der Zettel leer
 * wird – und der schlechteste Zeitpunkt, das zu bemerken, ist der, an dem man
 * die Codes gerade braucht.
 */
function ErsatzcodeVerwaltung() {
  const [stand, setStand] = useState<{ uebrig: number; gesamt: number } | null>(null);
  const [formOffen, setFormOffen] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [neue, setNeue] = useState<string[] | null>(null);

  const laden = () => {
    getErsatzcodeStand().then(setStand).catch(() => {});
  };

  useEffect(laden, []);

  const erzeugen = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { ersatzcodes } = await neueErsatzcodes(code);
      setNeue(ersatzcodes);
      setFormOffen(false);
      setCode('');
      laden();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Codes konnten nicht erzeugt werden.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  if (neue) {
    return <Ersatzcodes codes={neue} onFertig={() => setNeue(null)} />;
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h4 className="text-sm font-medium text-slate-900">Ersatzcodes</h4>
          <p className="mt-0.5 text-sm text-slate-500">
            {stand === null
              ? 'Wird geladen …'
              : stand.uebrig === 0
                ? 'Keine Ersatzcodes mehr übrig – bitte neue erzeugen.'
                : `Noch ${stand.uebrig} von ${stand.gesamt} Codes unbenutzt.`}
          </p>
        </div>
        {!formOffen && (
          <button
            type="button"
            onClick={() => setFormOffen(true)}
            className="rounded-md border border-slate-300 bg-white px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-100"
          >
            Neue erzeugen
          </button>
        )}
      </div>

      {stand !== null && stand.uebrig > 0 && stand.uebrig <= 2 && (
        <p className="mt-2 text-sm font-medium text-amber-700">
          Es sind fast keine mehr da. Erzeuge jetzt neue, solange du noch in die App kommst.
        </p>
      )}

      {formOffen && (
        <form onSubmit={erzeugen} className="mt-3 border-t border-slate-200 pt-3">
          <p className="mb-2 text-sm text-slate-600">
            Der alte Satz wird dabei ungültig – auch die noch unbenutzten Codes. Gib zur
            Bestätigung deinen aktuellen Code aus der App ein.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <input
              type="text"
              inputMode="numeric"
              pattern="[0-9]{6}"
              maxLength={6}
              value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
              required
              autoComplete="one-time-code"
              placeholder="000000"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-center font-mono text-lg tracking-[0.5em] text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100 sm:w-40"
            />
            <button
              type="submit"
              disabled={loading || code.length !== 6}
              className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50"
            >
              {loading ? 'Erzeuge …' : 'Neue Codes erzeugen'}
            </button>
            <button
              type="button"
              onClick={() => { setFormOffen(false); setCode(''); }}
              className="text-sm text-slate-500 hover:text-slate-700"
            >
              Abbrechen
            </button>
          </div>
        </form>
      )}
    </div>
  );
}

// ─── TOTP Setup ─────────────────────────────────────────────────────────────

function TwoFactorSetup({ onCodes }: { onCodes: (codes: string[]) => void }) {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [step, setStep] = useState<'idle' | 'scanning'>('idle');
  const [secret, setSecret] = useState('');
  const [qrSrc, setQrSrc] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const handleStartSetup = async () => {
    setLoading(true);
    try {
      const result = await setup2fa();
      setSecret(result.secret);
      setQrSrc(result.qrDataUrl);
      setStep('scanning');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Setup fehlgeschlagen.');
    } finally {
      setLoading(false);
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await verifySetup2fa(code);
      // Die Codes gehen nach OBEN, nicht in den Zustand dieser Komponente.
      //
      // Mit `totpEnabled: true` ersetzt die Seite diesen Einrichtungs-Ablauf
      // durch das Abschalten-Feld – diese Komponente verschwindet also genau in
      // dem Moment, in dem die Codes erscheinen müssten. Lagen sie hier, wären
      // sie mit ihr weg, und wiederholen lassen sie sich nicht.
      if (result.ersatzcodes) onCodes(result.ersatzcodes);
      setAuth(result.accessToken, result.user, result.csrfToken);
      toast.success('Authenticator-App aktiviert!');
      setStep('idle'); setCode(''); setSecret(''); setQrSrc('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Verifizierung fehlgeschlagen.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  if (step === 'idle') {
    return (
      <button onClick={handleStartSetup} disabled={loading}
        className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
        {loading ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : null}
        Authenticator-App einrichten
      </button>
    );
  }

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
        <h4 className="mb-3 font-medium text-blue-900">Schritt 1: QR-Code scannen</h4>
        <p className="mb-4 text-sm text-blue-700">Öffne deine Authenticator-App und scanne den QR-Code.</p>
        <QrCode src={qrSrc} />
        <div className="mt-4">
          <button type="button" onClick={() => setShowSecret(!showSecret)} className="text-xs text-blue-600 hover:underline">
            {showSecret ? 'Schlüssel verbergen' : 'Manuell eingeben'}
          </button>
          {showSecret && <div className="mt-2 rounded-md bg-white p-3 font-mono text-sm text-slate-900 break-all select-all border border-blue-200">{secret}</div>}
        </div>
      </div>
      <form onSubmit={handleVerify} className="rounded-lg border border-slate-200 bg-white p-4">
        <h4 className="mb-3 font-medium text-slate-900">Schritt 2: Code bestätigen</h4>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
          <div className="flex-1">
            <label htmlFor="setupCode" className="mb-1 block text-sm font-medium text-slate-700">Code</label>
            <input id="setupCode" type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code}
              onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoComplete="one-time-code"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-center font-mono text-lg tracking-[0.5em] text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="000000" />
          </div>
          <button type="submit" disabled={loading || code.length !== 6} className="w-full rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50 sm:w-auto">
            {loading ? 'Prüfe...' : 'Bestätigen'}
          </button>
        </div>
      </form>
      <button type="button" onClick={() => { setStep('idle'); setCode(''); setSecret(''); setQrSrc(''); }} className="text-sm text-slate-500 hover:text-slate-700">&larr; Abbrechen</button>
    </div>
  );
}

// ─── TOTP Disable ───────────────────────────────────────────────────────────

function TwoFactorDisable() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const [showForm, setShowForm] = useState(false);
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);

  const handleDisable = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    try {
      const result = await disable2fa(code);
      setAuth(useAuthStore.getState().accessToken!, result.user, useAuthStore.getState().csrfToken ?? undefined);
      toast.success('Authenticator-App deaktiviert.');
      setShowForm(false); setCode('');
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Deaktivierung fehlgeschlagen.');
      setCode('');
    } finally {
      setLoading(false);
    }
  };

  if (!showForm) {
    return (
      <div className="flex items-center gap-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-sm font-medium text-green-700">
          <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
          Aktiv
        </span>
        <button onClick={() => setShowForm(true)} className="rounded-md border border-red-200 px-3 py-1.5 text-sm font-medium text-red-600 hover:bg-red-50">Deaktivieren</button>
      </div>
    );
  }

  return (
    <form onSubmit={handleDisable} className="rounded-lg border border-red-200 bg-red-50 p-4">
      <p className="mb-3 text-sm text-red-700">Gib zur Bestätigung deinen aktuellen Code ein.</p>
      <div className="flex flex-wrap items-end gap-3">
        <input type="text" inputMode="numeric" pattern="[0-9]{6}" maxLength={6} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))} required autoComplete="one-time-code"
          className="w-full rounded-md border border-red-300 px-3 py-2 text-center font-mono text-lg tracking-[0.5em] text-slate-900 focus:border-red-400 focus:outline-none focus:ring-2 focus:ring-red-100 sm:w-40" placeholder="000000" />
        <button type="submit" disabled={loading || code.length !== 6} className="rounded-md bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700 disabled:opacity-50">{loading ? 'Prüfe...' : 'Deaktivieren'}</button>
        <button type="button" onClick={() => { setShowForm(false); setCode(''); }} className="text-sm text-slate-500 hover:text-slate-700">Abbrechen</button>
      </div>
    </form>
  );
}

// ─── Passkey / WebAuthn Verwaltung ──────────────────────────────────────────

function PasskeyManager() {
  const setAuth = useAuthStore((s) => s.setAuth);
  const confirm = useConfirm();
  const [credentials, setCredentials] = useState<WebauthnCredentialInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [registering, setRegistering] = useState(false);
  const [deviceName, setDeviceName] = useState('');
  const [showRegister, setShowRegister] = useState(false);

  const supportsWebAuthn = typeof window !== 'undefined' && !!window.PublicKeyCredential;

  useEffect(() => {
    loadCredentials();
  }, []);

  const loadCredentials = async () => {
    try {
      const creds = await getWebauthnCredentials();
      setCredentials(creds);
    } catch {
      // Ignorieren
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!supportsWebAuthn) {
      toast.error('Dein Browser unterstützt keine Passkeys.');
      return;
    }

    setRegistering(true);
    try {
      const options = await getWebauthnRegisterOptions();
      const credential = await navigator.credentials.create({ publicKey: options }) as PublicKeyCredential;
      if (!credential) throw new Error('Registrierung abgebrochen.');

      const result = await verifyWebauthnRegistration(credential, deviceName || 'Passkey');
      setAuth(result.accessToken, result.user, result.csrfToken);
      toast.success('Passkey registriert!');
      setShowRegister(false);
      setDeviceName('');
      await loadCredentials();
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : 'Registrierung fehlgeschlagen.';
      if (!msg.includes('abgebrochen') && !msg.includes('AbortError')) {
        toast.error(msg);
      }
    } finally {
      setRegistering(false);
    }
  };

  const handleDelete = async (cred: WebauthnCredentialInfo) => {
    const ok = await confirm({
      title: 'Passkey löschen?',
      message: <>Passkey <strong>"{cred.deviceName || 'Unbenannt'}"</strong> wirklich löschen?</>,
      confirmLabel: 'Löschen',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      const { user } = await deleteWebauthnCredential(cred.id);
      // Store aktualisieren, damit der "Aktiv"-Badge / 2FA-Hinweis nach dem
      // Entfernen des letzten Passkeys sofort stimmt.
      if (user) {
        const { accessToken, csrfToken } = useAuthStore.getState();
        setAuth(accessToken!, user, csrfToken ?? undefined);
      }
      toast.success('Passkey gelöscht.');
      await loadCredentials();
    } catch (err: unknown) {
      toast.error(err instanceof Error ? err.message : 'Löschen fehlgeschlagen.');
    }
  };

  if (!supportsWebAuthn) {
    return <p className="text-sm text-slate-400">Dein Browser unterstützt keine Passkeys/WebAuthn.</p>;
  }

  return (
    <div className="space-y-4">
      {/* Bestehende Credentials */}
      {loading ? (
        <div className="flex justify-center py-4">
          <span className="h-5 w-5 animate-spin rounded-full border-2 border-slate-300 border-t-blue-600" />
        </div>
      ) : credentials.length > 0 ? (
        <div className="divide-y divide-slate-100 rounded-lg border border-slate-200">
          {credentials.map((cred) => (
            <div key={cred.id} className="flex items-center justify-between gap-2 px-3 py-3 sm:px-4">
              <div className="flex min-w-0 items-center gap-3">
                <svg className="hidden h-5 w-5 shrink-0 text-slate-400 sm:block" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.864 4.243A7.5 7.5 0 0 1 19.5 10.5c0 2.92-.556 5.709-1.568 8.268M5.742 6.364A7.465 7.465 0 0 0 4.5 10.5a7.464 7.464 0 0 1-1.15 3.993m1.989 3.559A11.209 11.209 0 0 0 8.25 10.5a3.75 3.75 0 1 1 7.5 0c0 .527-.021 1.049-.064 1.565M12 10.5a14.94 14.94 0 0 1-3.6 9.75m6.633-4.596a18.666 18.666 0 0 1-2.485 5.33" />
                </svg>
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium text-slate-900">{cred.deviceName || 'Passkey'}</p>
                  <p className="truncate text-xs text-slate-400">
                    {new Date(cred.createdAt).toLocaleDateString('de-DE')}
                    {cred.transports && ` \u00b7 ${cred.transports}`}
                  </p>
                </div>
              </div>
              <button onClick={() => handleDelete(cred)}
                className="shrink-0 rounded-md px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50">
                Entfernen
              </button>
            </div>
          ))}
        </div>
      ) : (
        <p className="text-sm text-slate-400">Keine Passkeys registriert.</p>
      )}

      {/* Neuen Passkey registrieren */}
      {showRegister ? (
        <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 space-y-3">
          <div>
            <label htmlFor="deviceName" className="mb-1 block text-sm font-medium text-slate-700">Name des Geräts</label>
            <input id="deviceName" type="text" value={deviceName}
              onChange={(e) => setDeviceName(e.target.value)}
              placeholder="z.B. YubiKey 5, MacBook TouchID"
              className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm text-slate-900 focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleRegister} disabled={registering}
              className="flex items-center gap-2 rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700 disabled:opacity-50">
              {registering ? <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/30 border-t-white" /> : null}
              Passkey registrieren
            </button>
            <button onClick={() => { setShowRegister(false); setDeviceName(''); }}
              className="rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <button onClick={() => setShowRegister(true)}
          className="flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
          <svg className="h-4 w-4 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 4.5v15m7.5-7.5h-15" />
          </svg>
          Neuen Passkey hinzufügen
        </button>
      )}
    </div>
  );
}

// ─── Mein Tarif ─────────────────────────────────────────────────────────────
//
// Nur zum Nachsehen: einen Tarif wechselt der Betreiber, nicht der Kunde. Die
// gesperrten Funktionen stehen trotzdem mit da – sonst weiß man nicht, was es
// überhaupt gäbe und wonach man fragen könnte.
//
// Was hier steht, gilt für die Stammbäume, die MIR gehören. In fremden Bäumen
// zählt der Tarif ihres Besitzers.

// ─── Rundnachrichten ─────────────────────────────────────────────────────────
//
// Der Betreiber kann allen Konten schreiben. Zwei Arten, und nur eine davon
// lässt sich hier abbestellen:
//
//   * Betriebsnachrichten – Wartung, Störung, Sicherheit. Die kommen immer.
//     Wer sich davon abmelden könnte, stünde bei der nächsten angekündigten
//     Wartung vor einer Fehlermeldung, die er sich nicht erklären kann.
//   * Mitteilungen – Neuerungen und Hinweise. Die hier.
//
// Der Unterschied steht ausdrücklich da. „Benachrichtigungen aus" und dann
// doch eine E-Mail zu bekommen, wirkt sonst wie ein kaputter Schalter.

function RundnachrichtenKarte() {
  const queryClient = useQueryClient();
  const { data, isLoading } = useQuery({
    queryKey: ['rundnachrichten'],
    queryFn: getRundnachrichten,
  });

  const speichern = useMutation({
    mutationFn: setRundnachrichten,
    onSuccess: (r) => {
      queryClient.setQueryData(['rundnachrichten'], r);
      toast.success(
        r.abgemeldet ? 'Mitteilungen abbestellt.' : 'Mitteilungen wieder aktiviert.',
      );
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const an = !(data?.abgemeldet ?? false);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <h2 className="mb-2 text-base font-semibold text-slate-900">E-Mails vom Betreiber</h2>
      <p className="mb-4 text-sm text-slate-500">
        Nachrichten zum Betrieb – angekündigte Wartung, Störungen, Sicherheit –
        bekommst du in jedem Fall. Sie betreffen deinen Zugang unmittelbar.
      </p>

      <label className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={an}
          disabled={isLoading || speichern.isPending}
          onChange={(e) => speichern.mutate(!e.target.checked)}
          className="mt-0.5 h-4 w-4 rounded border-slate-300 disabled:opacity-50"
        />
        <span className="text-sm">
          <span className="font-medium text-slate-800">Mitteilungen erhalten</span>
          <span className="mt-0.5 block text-slate-500">
            Hinweise auf Neuerungen und Tipps zur Anwendung. Kannst du jederzeit
            wieder abstellen.
          </span>
        </span>
      </label>
    </section>
  );
}

function MeinTarifKarte() {
  const { data, isLoading, isError } = useQuery({
    queryKey: ['trees', 'my-plan'],
    queryFn: fetchMeinTarif,
  });

  if (isLoading || isError || !data) return null;

  const { plan, kontingent, features } = data;
  const freigeschaltet = new Set(plan.features);

  // Nicht stur in GB: 256 MB als „0,3 GB" zu schreiben liest sich falsch.
  // Die Zahl gilt für alle Stammbäume dieses Kontos zusammen.
  const speicher =
    plan.maxStorageBytes === null ? 'unbegrenzt' : groesse(plan.maxStorageBytes);

  return (
    <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
      <div className="mb-4 flex flex-wrap items-center justify-between gap-2">
        <h2 className="text-base font-semibold text-slate-900">Mein Tarif</h2>
        <span className="rounded-full bg-violet-50 px-2.5 py-0.5 text-xs font-medium text-violet-700">
          {plan.name}
        </span>
      </div>

      {plan.description && <p className="mb-4 text-sm text-slate-600">{plan.description}</p>}

      <dl className="space-y-2 text-sm">
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Eigene Stammbäume</dt>
          <dd className="text-right font-medium text-slate-900">
            {kontingent.unbegrenzt ? (
              'ohne Begrenzung'
            ) : (
              <>
                {kontingent.used} von {kontingent.limit}
                {kontingent.individuell && (
                  <span className="ml-1 font-normal text-slate-400">(eigene Vereinbarung)</span>
                )}
              </>
            )}
          </dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Speicher gesamt</dt>
          <dd className="font-medium text-slate-900">{speicher}</dd>
        </div>
        <div className="flex justify-between gap-2">
          <dt className="text-slate-500">Personen je Stammbaum</dt>
          <dd className="font-medium text-slate-900">
            {plan.maxPersons === null ? 'ohne Begrenzung' : plan.maxPersons.toLocaleString('de-DE')}
          </dd>
        </div>
      </dl>

      <h3 className="mb-2 mt-5 text-sm font-medium text-slate-700">Enthaltene Funktionen</h3>
      <ul className="grid gap-1.5 sm:grid-cols-2">
        {features.map((f) => {
          const an = freigeschaltet.has(f.key);
          return (
            <li key={f.key} className="flex items-start gap-2 text-sm" title={f.description}>
              <span
                aria-hidden="true"
                className={`mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full ${
                  an ? 'bg-emerald-500' : 'bg-slate-300'
                }`}
              />
              <span className={an ? 'text-slate-700' : 'text-slate-400 line-through'}>
                {f.label}
              </span>
            </li>
          );
        })}
      </ul>

      <p className="mt-4 text-xs text-slate-400">
        Für einen anderen Tarif wenden Sie sich an den Betreiber.
      </p>
    </section>
  );
}

// ─── SecuritySettingsPage ───────────────────────────────────────────────────

export function SecuritySettingsPage() {
  const user = useAuthStore((s) => s.user);

  /**
   * Die frisch erzeugten Ersatzcodes.
   *
   * Sie liegen HIER und nicht in der Einrichtungs-Komponente: mit dem
   * Einschalten wird die durch das Abschalten-Feld ersetzt, verschwindet also
   * genau in dem Moment, in dem die Codes erscheinen müssten. Genau so war es
   * zuerst gebaut – die Codes waren weg, bevor sie jemand gesehen hatte, und
   * ein zweites Mal gibt es sie nicht.
   */
  const [frischeCodes, setFrischeCodes] = useState<string[] | null>(null);

  return (
    <div className="flex h-screen flex-col bg-slate-50">
      <header className="flex h-14 shrink-0 items-center justify-between border-b border-slate-200 bg-white px-4 shadow-sm">
        <div className="flex items-center gap-2 sm:gap-4">
          <Link to="/" className="flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700">
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M10.5 19.5 3 12m0 0 7.5-7.5M3 12h18" />
            </svg>
            <span className="hidden sm:inline">Zurück zum Stammbaum</span>
          </Link>
          {/* Nicht mehr „Sicherheit": auf dieser Seite stehen längst auch
              Tarif und E-Mail-Einstellungen. Der Pfad bleibt, damit
              Lesezeichen nicht ins Leere laufen. */}
          <h1 className="text-base font-semibold text-slate-800 sm:text-lg">Einstellungen</h1>
        </div>
      </header>

      <main className="flex-1 overflow-y-auto p-4 sm:p-8">
        <div className="mx-auto w-full max-w-2xl space-y-6 sm:space-y-8">
          {/* Konto */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <h2 className="mb-4 text-base font-semibold text-slate-900">Konto</h2>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-slate-500">E-Mail</dt>
                <dd className="font-medium text-slate-900">{user?.email}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-slate-500">Rolle</dt>
                <dd className="font-medium text-slate-900">{user?.role === 'admin' ? 'Admin' : 'Betrachter'}</dd>
              </div>
            </dl>
          </section>

          {/* Tarif */}
          <MeinTarifKarte />

          {/* E-Mails vom Betreiber */}
          <RundnachrichtenKarte />

          {/* Passwort */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <h2 className="mb-2 text-base font-semibold text-slate-900">Passwort</h2>
            <p className="mb-4 text-sm text-slate-500">Ändere dein Passwort regelmäßig für mehr Sicherheit.</p>
            <Link to="/change-password" className="inline-flex items-center gap-2 rounded-md border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50">
              Passwort ändern
            </Link>
          </section>

          {/* 2FA Hinweis */}
          {!user?.totpEnabled && !user?.webauthnEnabled && (
            <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
              <p className="text-sm font-medium text-amber-800">Kein zweiter Faktor konfiguriert</p>
              <p className="mt-1 text-sm text-amber-700">
                Richte eine Authenticator-App oder einen YubiKey ein, um dein Konto besser zu schützen. Du kannst auch beide gleichzeitig aktivieren.
              </p>
            </div>
          )}

          {/* TOTP */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">Authenticator-App (TOTP)</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Zeitbasierter Code aus einer App wie Google Authenticator, Authy oder 1Password.
                </p>
              </div>
              {user?.totpEnabled && (
                <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 sm:inline-flex">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                  Aktiv
                </span>
              )}
            </div>
            {frischeCodes ? (
              // Nach dem Einschalten: der Kasten bleibt stehen, bis er
              // bestätigt wird. Er lässt sich nicht wegklicken, bevor jemand
              // die Codes gesichert hat – ein zweites Mal gibt es sie nicht.
              <Ersatzcodes codes={frischeCodes} onFertig={() => setFrischeCodes(null)} />
            ) : (
              <>
                {user?.totpEnabled ? (
                  <TwoFactorDisable />
                ) : (
                  <TwoFactorSetup onCodes={setFrischeCodes} />
                )}
                {user?.totpEnabled && (
                  <div className="mt-4">
                    <ErsatzcodeVerwaltung />
                  </div>
                )}
              </>
            )}
          </section>

          {/* Passkeys / WebAuthn */}
          <section className="rounded-lg border border-slate-200 bg-white p-4 shadow-sm sm:p-6">
            <div className="mb-4 flex items-center justify-between">
              <div>
                <h2 className="text-base font-semibold text-slate-900">YubiKey & Passkeys</h2>
                <p className="mt-1 text-sm text-slate-500">
                  Nutze einen YubiKey, TouchID, FaceID oder Windows Hello als zweiten Faktor.
                  Mehrere Schlüssel können gleichzeitig registriert sein.
                </p>
              </div>
              {user?.webauthnEnabled && (
                <span className="hidden shrink-0 items-center gap-1.5 rounded-full bg-green-50 px-3 py-1 text-xs font-medium text-green-700 sm:inline-flex">
                  <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}><path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" /></svg>
                  Aktiv
                </span>
              )}
            </div>
            <PasskeyManager />
          </section>
        </div>
      </main>
    </div>
  );
}
